<template>
  <div class="min-h-screen bg-gray-100 py-12 px-4">
    <div class="max-w-3xl mx-auto">
      <h1 class="text-2xl font-bold text-gray-900 mb-2">Homologação de Anfitriões</h1>

      <!-- Chave admin -->
      <div v-if="!adminKey" class="bg-white rounded-xl shadow p-6 mb-8">
        <label class="block text-sm font-medium text-gray-700 mb-2">Chave de administrador</label>
        <input
          v-model="keyInput"
          type="password"
          placeholder="Digite a chave"
          class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
          @keyup.enter="submitKey"
        />
        <button
          type="button"
          @click="submitKey"
          class="mt-3 w-full bg-indigo-600 text-white py-2 rounded-lg font-semibold hover:bg-indigo-700"
        >
          Entrar
        </button>
        <p v-if="keyError" class="mt-2 text-sm text-red-600">{{ keyError }}</p>
      </div>

      <template v-else>
        <div class="flex items-center justify-between mb-4">
          <span class="text-sm text-gray-600">Chave configurada</span>
          <button
            type="button"
            @click="clearKey"
            class="text-sm text-gray-500 hover:text-gray-700"
          >
            Sair
          </button>
        </div>

        <!-- Filtros -->
        <div class="bg-white rounded-xl shadow p-4 mb-6 space-y-4">
          <div class="flex flex-wrap gap-4 items-end">
            <div class="flex-1 min-w-[200px]">
              <label class="block text-sm font-medium text-gray-700 mb-1">Status</label>
              <select
                v-model="filterStatus"
                @change="fetchTenants"
                class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
              >
                <option value="all">Todos</option>
                <option value="active">Ativos</option>
                <option value="inactive">Inativos</option>
              </select>
            </div>
            <div class="flex-1 min-w-[200px]">
              <label class="block text-sm font-medium text-gray-700 mb-1">Buscar (nome ou ambiente)</label>
              <input
                v-model="searchQuery"
                type="text"
                placeholder="Ex: Daniel ou danielnicolau"
                class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                @keyup.enter="fetchTenants"
              />
            </div>
            <button
              type="button"
              @click="fetchTenants"
              class="px-4 py-2 bg-indigo-600 text-white rounded-lg font-medium hover:bg-indigo-700"
            >
              Buscar
            </button>
          </div>
        </div>

        <div v-if="loading" class="text-center py-8 text-gray-500">Carregando...</div>
        <div v-else-if="error" class="bg-red-50 text-red-700 px-4 py-3 rounded-lg mb-6">{{ error }}</div>
        <div v-else-if="tenants.length === 0" class="bg-white rounded-xl shadow p-8 text-center text-gray-500">
          Nenhum tenant encontrado com os filtros usados.
        </div>
        <ul v-else class="space-y-4">
          <li
            v-for="t in tenants"
            :key="t.id"
            class="bg-white rounded-xl shadow p-4"
          >
            <div class="flex flex-wrap items-start justify-between gap-4 mb-3">
              <div>
                <p class="font-semibold text-gray-900">{{ t.name }}</p>
                <p class="text-sm text-gray-500">{{ t.master_code }}</p>
                <p v-if="t.city || t.state" class="text-sm text-gray-500">
                  <span v-if="t.city">{{ t.city }}</span>
                  <span v-if="t.city && t.state"> - </span>
                  <span v-if="t.state">{{ t.state }}</span>
                </p>
                <p class="text-xs text-gray-400 mt-1">{{ formatDate(t.created_at) }}</p>
                <div v-if="t.phone" class="flex items-center gap-2 mt-2 mb-1">
                  <a
                    v-if="getWhatsAppLink(t.phone)"
                    :href="getWhatsAppLink(t.phone)"
                    target="_blank"
                    rel="noopener"
                    class="inline-flex items-center justify-center w-8 h-8 rounded-full bg-green-500 text-white hover:bg-green-600"
                    title="Abrir WhatsApp"
                  >
                    <i class="fab fa-whatsapp text-lg"></i>
                  </a>
                  <p class="text-sm text-gray-500">{{ t.phone }}</p>
                </div>
                
                <div v-if="t.instagram_profile" class="flex items-center gap-1 mb-1">
                  <button
                    type="button"
                    @click="openInNewTab(t.instagram_profile)"
                    class="inline-flex items-center justify-center w-8 h-8 rounded-full bg-pink-500 text-white hover:bg-pink-600"
                    title="Abrir Instagram"
                  >
                    <i class="fab fa-instagram text-lg"></i>
                  </button>
                  <span class="text-xs text-gray-500 max-w-[180px]">
                    {{ t.instagram_profile }}
                  </span>
                </div>
                <div v-if="t.facebook_profile" class="flex items-center gap-1">
                  <button
                    type="button"
                    @click="openInNewTab(t.facebook_profile)"
                    class="inline-flex items-center justify-center w-8 h-8 rounded-full bg-blue-600 text-white hover:bg-blue-700"
                    title="Abrir Facebook"
                  >
                    <i class="fab fa-facebook-f text-lg"></i>
                  </button>
                  <span class="text-xs text-gray-500 max-w-[180px]">
                    {{ t.facebook_profile }}
                  </span>
                </div>
              
              
              </div>
              <div class="flex items-center gap-2">
                <span
                  :class="[
                    'px-2 py-1 rounded text-xs font-medium',
                    t.active ? 'bg-green-100 text-green-800' : 'bg-amber-100 text-amber-800'
                  ]"
                >
                  {{ t.active ? 'Ativo' : 'Inativo' }}
                </span>
                <button
                  v-if="!t.active"
                  type="button"
                  @click="confirmActivate(t)"
                  :disabled="activatingId === t.id"
                  class="px-4 py-2 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 disabled:opacity-50 text-sm"
                >
                  {{ activatingId === t.id ? 'Ativando...' : 'Ativar' }}
                </button>
                <button
                  v-else
                  type="button"
                  @click="confirmDeactivate(t)"
                  :disabled="deactivatingId === t.id"
                  class="px-4 py-2 bg-amber-600 text-white rounded-lg font-medium hover:bg-amber-700 disabled:opacity-50 text-sm"
                >
                  {{ deactivatingId === t.id ? 'Inativando...' : 'Inativar' }}
                </button>
              </div>
            </div>

            <!-- Mensagem de rejeição (só para inativos) -->
            <div v-if="!t.active" class="pt-3 border-t border-gray-100">
              <label class="block text-sm font-medium text-gray-700 mb-1">Mensagem para o anfitrião (motivo da não aprovação ou orientação)</label>
              <textarea
                :value="getRejectionDraft(t)"
                @input="setRejectionDraft(t.id, $event.target.value)"
                rows="2"
                placeholder="Ex: Envie documentação da empresa por e-mail para aprovação."
                class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 text-sm"
              />
              <div class="flex justify-end gap-2 mt-2">
                <span v-if="savingMessageId === t.id" class="text-sm text-gray-500">Salvando...</span>
                <button
                  type="button"
                  @click="saveRejectionMessage(t)"
                  :disabled="savingMessageId === t.id"
                  class="px-3 py-1.5 bg-gray-700 text-white rounded-lg text-sm font-medium hover:bg-gray-800 disabled:opacity-50"
                >
                  Salvar mensagem
                </button>
              </div>
            </div>
          </li>
        </ul>
      </template>

      <div class="mt-8">
        <a href="/" class="text-indigo-600 hover:text-indigo-700 font-medium">← Voltar à vitrine</a>
      </div>
    </div>
  </div>
</template>

<script>
const ADMIN_KEY_STORAGE = 'tripday_admin_key'

export default {
  name: 'AdminPendingTenants',
  data() {
    return {
      keyInput: '',
      adminKey: typeof sessionStorage !== 'undefined' ? sessionStorage.getItem(ADMIN_KEY_STORAGE) : null,
      keyError: '',
      filterStatus: 'inactive',
      searchQuery: '',
      tenants: [],
      rejectionDrafts: {},
      loading: false,
      error: '',
      activatingId: null,
      deactivatingId: null,
      savingMessageId: null
    }
  },
  mounted() {
    if (this.adminKey) this.fetchTenants()
  },
  methods: {
    getWhatsAppLink(phone) {
      if (!phone) return null
      const digits = String(phone).replace(/\D/g, '')
      if (!digits) return null
      // Assume número brasileiro (55) por padrão; ajuste se precisar
      const withCountry = digits.length <= 11 ? `55${digits}` : digits
      return `https://wa.me/${withCountry}`
    },
    openInNewTab(url) {
      if (!url) return
      window.open(url, '_blank', 'noopener')
    },
    submitKey() {
      const key = this.keyInput.trim()
      if (!key) {
        this.keyError = 'Digite a chave.'
        return
      }
      this.keyError = ''
      sessionStorage.setItem(ADMIN_KEY_STORAGE, key)
      this.adminKey = key
      this.keyInput = ''
      this.fetchTenants()
    },
    clearKey() {
      sessionStorage.removeItem(ADMIN_KEY_STORAGE)
      this.adminKey = null
      this.tenants = []
    },
    buildQueryParams() {
      const p = new URLSearchParams()
      if (this.filterStatus && this.filterStatus !== 'all') p.set('status', this.filterStatus)
      if (this.searchQuery.trim()) p.set('search', this.searchQuery.trim())
      return p.toString()
    },
    async fetchTenants() {
      this.loading = true
      this.error = ''
      try {
        const qs = this.buildQueryParams()
        const url = qs ? `/api/v1/admin/tenants?${qs}` : '/api/v1/admin/tenants'
        const res = await fetch(url, {
          headers: { 'X-Admin-Key': this.adminKey }
        })
        const data = await res.json().catch(() => ({}))
        if (!res.ok) {
          if (res.status === 401) {
            this.clearKey()
            this.keyError = 'Chave inválida.'
          } else {
            this.error = data.error || 'Erro ao carregar.'
          }
          this.tenants = []
          return
        }
        this.tenants = data.tenants || []
      } catch (e) {
        this.error = 'Erro de conexão.'
        this.tenants = []
      } finally {
        this.loading = false
      }
    },
    setRejectionDraft(tenantId, value) {
      this.rejectionDrafts = { ...this.rejectionDrafts, [tenantId]: value }
    },
    getRejectionDraft(t) {
      return this.rejectionDrafts[t.id] !== undefined ? this.rejectionDrafts[t.id] : t.rejection_message
    },
    async saveRejectionMessage(t) {
      const message = this.rejectionDrafts[t.id] !== undefined ? this.rejectionDrafts[t.id] : t.rejection_message
      this.savingMessageId = t.id
      this.error = ''
      try {
        const res = await fetch(`/api/v1/admin/tenants/${t.id}/rejection_message`, {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            'X-Admin-Key': this.adminKey
          },
          body: JSON.stringify({ rejection_message: message || '' })
        })
        const data = await res.json().catch(() => ({}))
        if (!res.ok) {
          this.error = data.error || 'Erro ao salvar mensagem.'
          return
        }
        const idx = this.tenants.findIndex((x) => x.id === t.id)
        if (idx >= 0) this.tenants[idx] = { ...this.tenants[idx], rejection_message: data.tenant?.rejection_message ?? message }
        delete this.rejectionDrafts[t.id]
        this.rejectionDrafts = { ...this.rejectionDrafts }
      } finally {
        this.savingMessageId = null
      }
    },
    async activateTenant(t) {
      this.activatingId = t.id
      this.error = ''
      try {
        const res = await fetch(`/api/v1/admin/tenants/${t.id}/activate`, {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            'X-Admin-Key': this.adminKey
          }
        })
        const data = await res.json().catch(() => ({}))
        if (!res.ok) {
          this.error = data.error || 'Erro ao ativar.'
          return
        }
        this.tenants = this.tenants.filter((x) => x.id !== t.id)
      } finally {
        this.activatingId = null
      }
    },
    async deactivateTenant(t) {
      this.deactivatingId = t.id
      this.error = ''
      try {
        const res = await fetch(`/api/v1/admin/tenants/${t.id}/deactivate`, {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            'X-Admin-Key': this.adminKey
          }
        })
        const data = await res.json().catch(() => ({}))
        if (!res.ok) {
          this.error = data.error || 'Erro ao inativar.'
          return
        }
        const idx = this.tenants.findIndex((x) => x.id === t.id)
        if (idx >= 0) this.tenants[idx] = { ...data.tenant }
      } finally {
        this.deactivatingId = null
      }
    },
    confirmActivate(t) {
      if (confirm(`Ativar o ambiente \"${t.name}\" (${t.master_code})?`)) {
        this.activateTenant(t)
      }
    },
    confirmDeactivate(t) {
      if (confirm(`Inativar o ambiente \"${t.name}\" (${t.master_code})? O anfitrião não poderá fazer login e a vitrine não exibirá mais os imóveis.`)) {
        this.deactivateTenant(t)
      }
    },
    formatDate(iso) {
      if (!iso) return ''
      const d = new Date(iso)
      return d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })
    }
  }
}
</script>
